<template>
  <section>
    <BaseDialog
      v-if="simpleCreation"
      :width="'375px'"
      :show-text="false"
      :text-align="'left'"
      :footer-align="'flex-end'"
      :title="$t('adminTitles.EnterPlanName')"
      :confirm-button-text="$t('adminTitles.continue')"
      :cancel-button-text="$t('buttons.cancel')"
      :confirm-loading="btnLoading"
      @close="$emit('close')"
      @confirm="submitForm"
    >
      <el-form :model="form" :rules="rules" ref="form" label-position="top"
               class="mb_32">
        <el-form-item class="dialog-modal__input" :label="$t('adminTitles.planTitle')" prop="name">
          <el-input :placeholder="$t('adminTitles.YourPlan')" v-model="form.name"/>
        </el-form-item>
      </el-form>
    </BaseDialog>
    <BaseSideModal v-if="!simpleCreation" :width="'800px'" @close="onClose"
                   :confirm-close-dialog="true">
      <template #title>
        {{
          programEdit ? $t('adminTitles.editPlan') : $t('adminTitles.createNewIndividualPlan')
        }}
      </template>

      <el-form :model="form" :rules="rules" ref="form" label-position="top"
               class="modal-plan__form">
        <el-form-item class="modal-plan__input" :label="$t('adminTitles.planTitle')" prop="name">
          <el-input :placeholder="$t('adminTitles.createPlanTitle')" v-model="form.name"/>
        </el-form-item>
        <el-form-item
          class="modal-plan__input"
          :label="$t('adminTitles.mainProgramLanguage')"
          prop="defaultLocale"
        >
          <el-select
            v-model="form.defaultLocale"
            filterable
            :placeholder="$t('adminTitles.createPlanLanguage')"
          >
            <el-option v-for="item in languages" :key="item.id" :label="item.title"
                       :value="item.id">
            </el-option>
          </el-select>
          <BaseTooltip
            class="program__input-tooltip"
            :tooltip-text="$t('adminTitles.createPlanLanguageTooltip')"
          />
        </el-form-item>

        <el-form-item
          class="modal-plan__input"
          :label="$t('adminTitles.coverageCommission')"
          prop="commissionPercentage"
        >
          <div class="commission-input">
            <el-input
              v-model="form.commissionPercentage"
              type="number"
              min="0"
              max="100"
              :disabled="!isUserAdmin"
              :placeholder="$t('adminTitles.coverageCommissionPlaceholder')"
            />
            <span
              class="commission-input__suffix"
              :class="{ 'commission-input__suffix--disabled': !isUserAdmin }"
            >%</span>
          </div>
        </el-form-item>

        <el-form-item
          class="modal-plan__input"
          :label="$t('adminTitles.coverageAseguradora')"
          prop="insuranceCompanyId"
        >
          <el-select
            v-model="form.insuranceCompanyId"
            filterable
            clearable
            :disabled="!isUserAdmin"
            :placeholder="$t('adminTitles.SelectInsuranceCompany')"
            :no-data-text="$t('adminTitles.insuranceCompanyNotFound')"
            @change="onAseguradoraChange"
          >
            <el-option
              :label="$t('adminTitles.CreateNewCareer')"
              :value="ADD_NEW_CARRIER"
            />
            <el-option
              v-for="item in activeInsuranceCompanies"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            />
          </el-select>
        </el-form-item>

        <p class="modal-plan__caption">{{ $t('adminTitles.coverageCommissionHint') }}</p>
      </el-form>

      <NewInsuranceCompanyModal
        v-if="addNewCarrierModalOpen"
        :simple-creation="true"
        @close="addNewCarrierModalOpen = false"
        @onCompanyCreate="onCompanyCreate($event.insuranceCompanyId)"
      />

      <template #footer>
        <BaseButton type="outline" @click="onClose">
          {{ $t('buttons.close') }}
        </BaseButton>
        <BaseButton :loading="btnLoading" @click="submitForm">
          {{ programEdit ? $t('buttons.save') : $t('buttons.create') }}
        </BaseButton>
      </template>
    </BaseSideModal>
  </section>
</template>

<script>
import {mapGetters} from 'vuex';
import {PROGRAM_SAVE, PROGRAMS_LIST_REQUEST} from '@/store/actions/programs';
import BaseSideModal from '@/views/RenovaDashboard/components/ui-kit/BaseSideModal/BaseSideModal';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import BaseTooltip from '@/views/RenovaDashboard/components/ui-kit/BaseTooltip';
import {
  PLAN_TYPES,
  PlanTypeOptions,
} from '@/views/RenovaDashboard/ProgramsParts/components/constants';
import BaseDialog from '@/views/RenovaDashboard/components/ui-kit/BaseDialog';
import NewInsuranceCompanyModal
  from '@/views/RenovaDashboard/SettingsParts/parts/InsuranceCompanies/components/NewInsuranceCompanyModal';
import {USER_ROLES} from '@/store/modules/user';

const ADD_NEW_CARRIER = 'addNewCarrier';

export default {
  name: 'ModalCreateNewIndividualPlan',
  components: {
    BaseDialog,
    // BaseDropdown,
    BaseTooltip,
    BaseButton,
    BaseSideModal,
    NewInsuranceCompanyModal,
  },
  mixins: [PlanTypeOptions],
  emits: ['close', 'saved'],
  props: {
    programEdit: {
      type: Object,
      default: null,
    },
    insuranceCompanyId: {
      type: String,
      default: null,
    },
    simpleCreation: {
      type: Boolean,
      default: false,
    },
    /** Prefilled plan name, e.g. the product name an OCR'd policy prints. */
    initialName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      ADD_NEW_CARRIER,
      addNewCarrierModalOpen: false,
      form: {
        name: '',
        insuranceCompanyId: null,
        commissionPercentage: null,
        defaultLocale: null,
        // insuranceTypeSave: null,
      },
      btnLoading: false,
      rules: {
        name: [
          {
            required: true,
            message: this.$t('adminTitles.FieldIsRequired'),
          },
        ],
        // insuranceTypeSave: [
        //   {
        //     required: true,
        //     message: this.$t('adminTitles.FieldIsRequired'),
        //   },
        // ],
      },
      showConfirmationDialog: false,
    };
  },
  created() {
    if (!this.programEdit && this.initialName) {
      this.form.name = this.initialName;
    }
    if (this.programEdit) {
      this.form = {
        packageId: this.programEdit.packageId,
        name: this.programEdit.name,
        insuranceCompanyId: this.programEdit.insuranceCompany?.id || null,
        commissionPercentage: this.programEdit.commissionPercentage ?? null,
        defaultLocale: this.programEdit.defaultLocale,
        // insuranceTypeSave: this.programEdit.insuranceType,
      };
    }
  },
  watch: {
    // activeInsuranceCompanies(val) {
    //   if (val.length === 1) {
    //     this.form.insuranceCompanyId = val[0].id;
    //   }
    // },
  },
  computed: {
    ...mapGetters([
      'programDetails',
      'activeInsuranceCompanies',
      'languages',
      'userRole',
    ]),
    isUserAdmin() {
      return this.userRole === USER_ROLES.ADMIN;
    },
    datepickerOptions() {
      return {
        firstDayOfWeek: 1,
      };
    },
    haveUnsavedData() {
      return Object.values(this.form)
        .some((value) => value);
    },
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    onAseguradoraChange(value) {
      if (value === ADD_NEW_CARRIER) {
        // Don't keep the sentinel selected; open the inline create modal instead.
        this.form.insuranceCompanyId = null;
        this.addNewCarrierModalOpen = true;
      }
    },
    onCompanyCreate(insuranceCompanyId) {
      this.form.insuranceCompanyId = insuranceCompanyId;
      this.addNewCarrierModalOpen = false;
    },
    buildAdminFieldsPayload() {
      // Comisión (%) and Aseguradora are admin-only. Only an admin's form may send these
      // fields; the backend rejects changes to them from non-admins.
      if (!this.isUserAdmin) return {};
      const payload = {};
      const commission = this.form.commissionPercentage;
      if (commission !== null && commission !== '' && commission !== undefined) {
        payload.commissionPercentageSave = Number(commission);
      } else if (this.programEdit) {
        payload.commissionPercentageDelete = true;
      }
      if (this.form.insuranceCompanyId) {
        payload.insuranceCompanyIdSave = this.form.insuranceCompanyId;
      } else if (this.programEdit) {
        payload.insuranceCompanyIdDelete = true;
      }
      return payload;
    },
    submitForm() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.btnLoading = true;
          const {
            commissionPercentage,
            insuranceCompanyId,
            ...formData
          } = this.form;
          this.$store
            .dispatch(PROGRAM_SAVE, {
              ...formData,
              ...this.buildAdminFieldsPayload(),
              insurancePlanTypeSave: PLAN_TYPES.INDIVIDUAL,
            })
            .then(async (res) => {
              await this.$store.dispatch(PROGRAMS_LIST_REQUEST);
              this.$emit('saved', res);
              this.$notify.success({
                title: `${this.$t('adminTitles.success')}`,
              });
              this.btnLoading = false;
              if (!this.simpleCreation) {
                this.$router.push(`/admin/programs/${res.packageId}`);
              }
            })
            .catch(() => {
              this.btnLoading = false;
              this.$notify.error({
                title: `${this.$t('adminTitles.someError')}`,
              });
            });
        }
      });
    },
  },
};
</script>

<style scoped>
::v-deep .el-select {
  display: inline;
}

.modal-plan__form {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.modal-plan__input {
  flex-basis: calc(50% - 8px);
  position: relative;
}

.modal-plan__caption {
  flex-basis: 100%;
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: var(--solid-dark-purple);
}

.commission-input {
  position: relative;
  width: 100%;
}

.commission-input__suffix {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  line-height: 1;
  color: #303133;
  pointer-events: none;
}

.commission-input__suffix--disabled {
  color: #c0c4cc;
}

/* Keep the value clear of the % glyph and drop the native number spinners. */
::v-deep .commission-input .el-input__inner {
  padding-right: 28px;
}

::v-deep .commission-input input[type='number']::-webkit-outer-spin-button,
::v-deep .commission-input input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

::v-deep .commission-input input[type='number'] {
  -moz-appearance: textfield;
}

.program__input-tooltip {
  position: absolute;
  right: 0;
  top: -22px;
}

.dialog-modal__input {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .el-form-item__content {
    width: 100%;
  }
}
@media screen and (max-width: 668px){
  .modal-plan__form{
    display: flex;
    flex-direction: column;
  }
}
</style>
